import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import Paper from '@base/Paper/Paper';
import Image from '@base/Image';
import Button from '@base/Button/Button';
import H3 from '@base/H3/H3';
import H5 from '@base/H5/H5';
import useWindowSize from '@hooks/useWindowSize';
import { getActiveUidsByType } from '@api/selectors/subscription';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { REFERRAL_TOUCHPOINT_CLICKED } from '@constants/segment';

const Container = styled(Paper)`
  max-width: 1136px;
  display: flex;
  width: 100%;
  padding: ${Spacings.SPACING_0B};
  margin: ${Spacings.SPACING_0B} auto;
  justify-content: space-between;
  margin-top: ${({ $newGoalPage }) =>
    $newGoalPage ? Spacings.SPACING_10B : Spacings.SPACING_30B};
  margin-bottom: ${({ $newGoalPage }) =>
    $newGoalPage ? Spacings.SPACING_10B : Spacings.SPACING_0B};
  ${mediaQuery.uptoTablet} {
    width: calc(100% - 32px);
  }
  ${mediaQuery.uptoMobile} {
    width: calc(100% - 32px);
    flex-direction: column-reverse;
  }
`;

const ContentSection = styled.div`
  padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_10B};
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
`;

const Heading = styled(H3)`
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.H4};
  }
`;
const SubHeading = styled(H5)`
  font-weight: ${Fonts.LIGHT};
  color: var(--color-text-secondary);
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.LIGHT};
    font-size: ${Fonts.H6};
  }
`;

const StyButton = styled(Button)`
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.HEAVY};
    font-size: ${Fonts.H7};
    min-height: ${Spacings.SPACING_8B};
    height: ${Spacings.SPACING_8B};
    width: 108px;
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 360px;
  height: 200px;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_9B};
    width: 50px;
    height: 50px;
    margin-left: ${Spacings.SPACING_4B};
  }
`;

const ReferralBanner = ({
  block: { extraBlockInfo } = {},
  newGoalPage,
  propGoalUID,
  className,
  iconUrl: imageUrl
}) => {
  const router = useRouter();
  let { goalUID } = router.query;
  if (propGoalUID) {
    goalUID = propGoalUID;
  }
  const goalSegmentData = useGoalEventProps({ goalUID });
  const {
    blockHeader: header,
    blockSubHeader: subHeader,
    ctaDetails: { label } = {},
    iconUrl: iconUrlSrc
  } = extraBlockInfo || {};

  const latestUserSubscriptions = useMyInfo(getActiveUidsByType);
  const { isMobile } = useWindowSize();
  let iconUrl = iconUrlSrc || 'referrals/presubscribe-refer-banner.svg';
  if (isMobile) {
    if (imageUrl) {
      iconUrl = imageUrl;
    } else {
      iconUrl = 'referrals/amazonVoucherMobile.svg';
    }
  }
  const handleReferFriend = () => {
    if (newGoalPage) {
      pushRoute('/referral', '/referral');
    } else {
      pushRoute('/settings/referral', '/settings/referral');
    }
    segment.track(REFERRAL_TOUCHPOINT_CLICKED, {
      ...goalSegmentData,
      last_primary_source_section: 'Planner Refer Side Card',
      referral_type: latestUserSubscriptions?.length ? 'Paid' : 'Free'
    });
  };

  return (
    <Container $newGoalPage={newGoalPage} className={className}>
      <ContentSection className="referral-banner-content">
        <Heading>
          {header || 'Refer to win Plus subscription and Amazon voucher'}
        </Heading>
        <SubHeading>
          {subHeader ||
            'For every successful referral win up to ₹2,500. Also, get 1 month Plus subscription once'}
        </SubHeading>
        <StyButton
          label={label || 'Refer now'}
          color="black"
          grid={3}
          onClick={handleReferFriend}
          className="referral-banner-button"
        />
      </ContentSection>
      <ImageSection className="banner-image-icon">
        <Image src={iconUrl} alt="referral" layout="fill" />
      </ImageSection>
    </Container>
  );
};

export default ReferralBanner;
